import React, { useEffect, useState } from "react";
import { Check, Trash2, Plus, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api, formatApiError, API } from "../../lib/api";
import { NUM2, fmtKwh, fmtKwp } from "../../lib/format";
import { Field, computeAggregates } from "./utils";
import TariffsSection from "./Tariffs";
import { useFeatures } from "../../context/FeatureContext";
import DateInput from "../../components/DateInput";

export default function DesignTab({ project, design, onSaved }) {
  const blank = {
    project_id: project.id,
    solaredge_url: design?.solaredge_url || "",
    source: design?.source || "manual",
    systemSize: design?.systemSize || 0,
    numberOfModules: design?.numberOfModules || 0,
    moduleModel: design?.moduleModel || "",
    moduleWattage: design?.moduleWattage || 0,
    inverterModel: design?.inverterModel || "",
    inverterCount: design?.inverterCount || 1,
    tilt: design?.tilt ?? 35,
    azimuth: design?.azimuth ?? 180,
    annualEnergyProduction: design?.annualEnergyProduction || 0,
    specificYield: design?.specificYield || 0,
    performanceRatio: design?.performanceRatio || 0.82,
    systemLossesPct: design?.systemLossesPct ?? 14,
    shadingPct: design?.shadingPct ?? 3,
    address: design?.address || project.address,
    lat: design?.lat || "",
    lon: design?.lon || "",
    notes: design?.notes || "",
  };
  const [form, setForm] = useState(blank);
  const { canUse } = useFeatures();
  const [arrays, setArrays] = useState(design?.arrays || []);
  const [busy, setBusy] = useState(false);
  // Commissioning fields live on the Project (not the Design). Editing here
  // fires PATCH /api/projects/{pid} — see saveCommissioning().
  const [installDate, setInstallDate] = useState(project.installation_date || "");
  const [degPct, setDegPct] = useState(
    project.annual_degradation_pct == null ? 0.5 : project.annual_degradation_pct,
  );
  const [savingCommissioning, setSavingCommissioning] = useState(false);
  const upd = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const saveCommissioning = async () => {
    setSavingCommissioning(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        installation_date: installDate || null,
        annual_degradation_pct: degPct === "" || degPct == null ? null : Number(degPct),
      });
      toast.success("Commissioning info saved — degradation will apply from next sync");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingCommissioning(false); }
  };

  // Auto-aggregate when arrays change
  const aggregates = arrays.length > 0 ? computeAggregates(arrays) : null;

  // auto-derive systemSize / annual if numberOfModules + moduleWattage + specificYield filled (single-array mode)
  useEffect(() => {
    if (arrays.length === 0 && form.numberOfModules > 0 && form.moduleWattage > 0) {
      const kwp = +(form.numberOfModules * form.moduleWattage / 1000).toFixed(2);
      if (Math.abs(kwp - form.systemSize) > 0.05) {
        setForm(f => ({ ...f, systemSize: kwp }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.numberOfModules, form.moduleWattage]);

  useEffect(() => {
    if (arrays.length === 0 && form.systemSize > 0 && form.specificYield > 0 && !form.annualEnergyProduction) {
      setForm(f => ({ ...f, annualEnergyProduction: Math.round(form.systemSize * form.specificYield) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.systemSize, form.specificYield]);

  const migrateToArrays = async () => {
    try {
      const { data } = await api.post(`/designs/migrate-to-arrays/${project.id}`);
      setArrays(data.arrays || []);
      toast.success("Migrated to multi-array");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addArray = () => {
    setArrays(a => [...a, {
      id: `tmp-${Date.now()}`,
      name: `Array ${a.length + 1}`,
      numberOfModules: 0,
      moduleModel: form.moduleModel || "",
      moduleWattage: form.moduleWattage || 0,
      tilt: 35, azimuth: 180, shadingPct: 3,
      annualEnergyProduction: 0,
      specificYield: 0,
    }]);
  };
  const updateArray = (id, field, value) => {
    setArrays(a => a.map(x => x.id === id ? {
      ...x,
      [field]: ["numberOfModules", "moduleWattage", "tilt", "azimuth", "shadingPct", "annualEnergyProduction", "specificYield"].includes(field)
        ? Number(value) : value,
    } : x));
  };
  const removeArray = (id) => setArrays(a => a.filter(x => x.id !== id));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        arrays: arrays.length > 0 ? arrays : undefined,
        lat: form.lat ? Number(form.lat) : null,
        lon: form.lon ? Number(form.lon) : null,
      };
      await api.post("/designs", payload);
      toast.success("Design saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="design-tab">
      <DesignVariantsBar projectId={project.id} currentDesignId={design?.id} onSwitched={onSaved} />
      <DesignImagesGallery projectId={project.id} />
      <section className="swiss-card-flat p-6">
        <div className="label mb-4">SolarEdge Designer reference</div>
        <div className="flex gap-2">
          <input
            data-testid="design-se-url"
            className="input-box flex-1 font-mono text-xs"
            placeholder="https://designer.solaredge.com/public/.../proposal/..."
            value={form.solaredge_url}
            onChange={upd("solaredge_url")}
          />
          {form.solaredge_url && (
            <a href={form.solaredge_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="design-open-se">
              <ExternalLink size={14} /> Open
            </a>
          )}
        </div>
        <p className="muted text-xs mt-3 flex items-center gap-1">
          <AlertTriangle size={11} className="text-[var(--signal)]" />
          For automated extraction of values from the SolarEdge proposal, use “Import via AI” on the New Project page.
        </p>
      </section>

      {/* Commissioning & degradation — Project-level fields (not Design) */}
      <section className="swiss-card-flat p-6" data-testid="commissioning-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label">Commissioning &amp; degradation</div>
            <div className="muted text-xs mt-1">
              Add the installation date to drive the Year-N degradation factor applied to
              simulated production. Values here save straight to the project record.
            </div>
          </div>
          <button onClick={saveCommissioning} disabled={savingCommissioning}
                  className="btn-accent" data-testid="save-commissioning-btn">
            {savingCommissioning ? "Saving…" : "Save commissioning"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label block mb-1">Installation date</label>
            <DateInput className="input-swiss font-mono"
                   value={installDate} onChange={(e) => setInstallDate(e.target.value)}
                   data-testid="commissioning-install-date" />
            <div className="text-[11px] muted mt-1">
              {installDate ? "Year 1 runs from this anniversary." : "No date set — degradation is not applied."}
            </div>
          </div>
          <div>
            <label className="label block mb-1">Annual degradation (%/yr)</label>
            <input type="number" step="0.01" min="0" max="5" className="input-swiss font-mono"
                   value={degPct} onChange={(e) => setDegPct(e.target.value)}
                   data-testid="commissioning-deg-pct" />
            <div className="text-[11px] muted mt-1">Industry norm: 0.5%. Higher for older panels.</div>
          </div>
          <div>
            <label className="label block mb-1">Year-1 simulated (kWh/yr)</label>
            <input type="text" readOnly className="input-swiss font-mono opacity-70"
                   value={form.annualEnergyProduction
                     ? form.annualEnergyProduction.toLocaleString()
                     : "— set below —"}
                   data-testid="commissioning-year1-mirror" />
            <div className="text-[11px] muted mt-1">Pulled from the design below. Edit there.</div>
          </div>
        </div>

        {installDate && form.annualEnergyProduction > 0 && (
          <div className="mt-5 bg-[var(--surface-2)] p-4 border border-[var(--line)]"
               data-testid="commissioning-preview">
            <div className="label mb-2">Projected annual production (post-degradation)</div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs font-mono">
              {[1, 5, 10, 15, 20, 25].map((yr) => {
                const deg = Number(degPct) || 0.5;
                const factor = yr === 1 ? 1 : (1 - deg / 100) ** (yr - 1);
                const kwh = Number(form.annualEnergyProduction) * factor;
                return (
                  <div key={yr} className="p-2 bg-white border border-[var(--line)]">
                    <div className="muted text-[10px] uppercase">Year {yr}</div>
                    <div className="text-[var(--ink)] font-bold">
                      {kwh > 999 ? `${(kwh / 1000).toFixed(1)} MWh` : `${kwh.toFixed(0)} kWh`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Tariff schedule editor — time-varying PPA / Export / Import rates */}
      {canUse("tariffs") && <TariffsSection project={project} />}

      {/* Multi-array section */}
      <section className="swiss-card-flat p-6" data-testid="arrays-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label">Sub-arrays</div>
            <div className="muted text-xs mt-1">
              {arrays.length === 0
                ? "Single-array project. Add multiple arrays for split roofs / orientations."
                : `${arrays.length} sub-array${arrays.length === 1 ? "" : "s"} · totals computed below.`}
            </div>
          </div>
          <div className="flex gap-2">
            {arrays.length === 0 && design?.numberOfModules > 0 && (
              <button onClick={migrateToArrays} className="btn-ghost" data-testid="migrate-arrays-btn">
                Migrate to multi-array
              </button>
            )}
            <button onClick={addArray} className="btn-accent" data-testid="add-array-btn">
              <Plus size={13} /> Add array
            </button>
          </div>
        </div>

        {arrays.length > 0 && (
          <div className="space-y-3">
            {arrays.map((a, idx) => (
              <div key={a.id} className="border border-[var(--line)] p-4 bg-[var(--surface)]" data-testid={`array-card-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <input
                    className="input-swiss font-medium max-w-xs"
                    value={a.name}
                    onChange={(e) => updateArray(a.id, "name", e.target.value)}
                    data-testid={`array-name-${idx}`}
                  />
                  <button onClick={() => removeArray(a.id)} data-testid={`array-remove-${idx}`}>
                    <Trash2 size={14} className="muted" />
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Field label="Modules" testid={`array-modules-${idx}`}><input className="input-swiss" type="number" value={a.numberOfModules} onChange={(e) => updateArray(a.id, "numberOfModules", e.target.value)} /></Field>
                  <Field label="Module model" testid={`array-model-${idx}`}><input className="input-swiss" value={a.moduleModel || ""} onChange={(e) => updateArray(a.id, "moduleModel", e.target.value)} /></Field>
                  <Field label="Module Wp" testid={`array-watt-${idx}`}><input className="input-swiss" type="number" value={a.moduleWattage} onChange={(e) => updateArray(a.id, "moduleWattage", e.target.value)} /></Field>
                  <Field label="kWp" testid={`array-kwp-${idx}`}><div className="font-mono text-sm pt-2">{NUM2.format((a.numberOfModules * a.moduleWattage) / 1000)}</div></Field>
                  <Field label="Tilt °" testid={`array-tilt-${idx}`}><input className="input-swiss" type="number" value={a.tilt} onChange={(e) => updateArray(a.id, "tilt", e.target.value)} /></Field>
                  <Field label="Azimuth °" testid={`array-azimuth-${idx}`}><input className="input-swiss" type="number" value={a.azimuth} onChange={(e) => updateArray(a.id, "azimuth", e.target.value)} /></Field>
                  <Field label="Annual kWh" testid={`array-annual-${idx}`}><input className="input-swiss" type="number" value={a.annualEnergyProduction} onChange={(e) => updateArray(a.id, "annualEnergyProduction", e.target.value)} /></Field>
                  <Field label="Shading %" testid={`array-shading-${idx}`}><input className="input-swiss" type="number" step="0.1" value={a.shadingPct} onChange={(e) => updateArray(a.id, "shadingPct", e.target.value)} /></Field>
                </div>
              </div>
            ))}

            {/* Aggregates */}
            {aggregates && (
              <div className="border-t-2 border-[var(--ink)] mt-4 pt-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="array-aggregates">
                <div><div className="label">Total system</div><div className="font-display text-xl font-semibold">{fmtKwp(aggregates.systemSize)}</div></div>
                <div><div className="label">Total modules</div><div className="font-display text-xl font-semibold">{aggregates.numberOfModules}</div></div>
                <div><div className="label">Total annual</div><div className="font-display text-xl font-semibold">{fmtKwh(aggregates.annualEnergyProduction)}</div></div>
                <div><div className="label">Avg specific yield</div><div className="font-display text-xl font-semibold">{NUM2.format(aggregates.specificYield)}</div></div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="swiss-card-flat p-6">
        <div className="label mb-4">System specification {arrays.length > 0 && <span className="ml-2 text-[10px] muted normal-case tracking-normal">(read-only — derived from sub-arrays)</span>}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Number of modules" testid="d-modules"><input className="input-swiss" type="number" value={arrays.length ? (aggregates?.numberOfModules || 0) : form.numberOfModules} onChange={upd("numberOfModules")} disabled={arrays.length > 0} /></Field>
          <Field label="Module model" testid="d-module-model"><input className="input-swiss" value={arrays.length ? (aggregates?.moduleModel || "") : form.moduleModel} onChange={upd("moduleModel")} disabled={arrays.length > 0} placeholder="e.g. SolarEdge SE410" /></Field>
          <Field label="Module wattage (Wp)" testid="d-module-watt"><input className="input-swiss" type="number" value={arrays.length ? (aggregates?.moduleWattage || 0) : form.moduleWattage} onChange={upd("moduleWattage")} disabled={arrays.length > 0} /></Field>
          <Field label="System size (kWp)" testid="d-system-size"><input className="input-swiss font-mono" type="number" step="0.01" value={arrays.length ? (aggregates?.systemSize || 0) : form.systemSize} onChange={upd("systemSize")} disabled={arrays.length > 0} /></Field>
          <Field label="Inverter model" testid="d-inverter"><input className="input-swiss" value={form.inverterModel} onChange={upd("inverterModel")} placeholder="e.g. SolarEdge SE10K" /></Field>
          <Field label="Inverter count" testid="d-inverter-count"><input className="input-swiss" type="number" value={form.inverterCount} onChange={upd("inverterCount")} /></Field>
        </div>
      </section>

      <section className="swiss-card-flat p-6">
        <div className="label mb-4">Geometry · location · yields</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Tilt (°)" testid="d-tilt"><input className="input-swiss" type="number" value={arrays.length ? (aggregates?.tilt || 0) : form.tilt} onChange={upd("tilt")} disabled={arrays.length > 0} /></Field>
          <Field label="Azimuth (° from N)" testid="d-azimuth"><input className="input-swiss" type="number" value={arrays.length ? (aggregates?.azimuth || 0) : form.azimuth} onChange={upd("azimuth")} disabled={arrays.length > 0} /></Field>
          <Field label="Latitude" testid="d-lat"><input className="input-swiss font-mono text-xs" value={form.lat} onChange={upd("lat")} placeholder="51.5074" /></Field>
          <Field label="Longitude" testid="d-lon"><input className="input-swiss font-mono text-xs" value={form.lon} onChange={upd("lon")} placeholder="-0.1278" /></Field>
          <Field label="Annual energy (kWh)" testid="d-annual"><input className="input-swiss" type="number" value={arrays.length ? (aggregates?.annualEnergyProduction || 0) : form.annualEnergyProduction} onChange={upd("annualEnergyProduction")} disabled={arrays.length > 0} /></Field>
          <Field label="Specific yield (kWh/kWp)" testid="d-specific-yield"><input className="input-swiss" type="number" value={arrays.length ? (aggregates?.specificYield || 0) : form.specificYield} onChange={upd("specificYield")} disabled={arrays.length > 0} /></Field>
          <Field label="Performance ratio" testid="d-pr"><input className="input-swiss" type="number" step="0.01" value={form.performanceRatio} onChange={upd("performanceRatio")} /></Field>
          <Field label="System losses (%)" testid="d-losses"><input className="input-swiss" type="number" step="0.1" value={form.systemLossesPct} onChange={upd("systemLossesPct")} /></Field>
          <Field label="Shading (%)" testid="d-shading"><input className="input-swiss" type="number" step="0.1" value={form.shadingPct} onChange={upd("shadingPct")} /></Field>
          <div className="md:col-span-3">
            <Field label="Notes" testid="d-notes"><textarea className="input-box" rows={2} value={form.notes} onChange={upd("notes")} /></Field>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={save} disabled={busy} className="btn-accent" data-testid="d-save-btn">
            <Check size={14} /> {busy ? "Saving…" : "Save design"}
          </button>
        </div>
      </section>

      {design?.annualEnergyProduction > 0 && (
        <section className="swiss-card-flat p-6">
          <div className="label mb-3">Monthly production forecast</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={(design.monthlyDistribution || []).map((s, i) => ({
                month: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i],
                kwh: Math.round(s * design.annualEnergyProduction),
              }))}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12 }} formatter={(v) => `${v} kWh`} />
                <Area dataKey="kwh" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.25} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
}

function DesignImagesGallery({ projectId }) {
  const [images, setImages] = useState([]);
  const base = API.replace(/\/api$/, "");

  useEffect(() => {
    api.get(`/documents/by-project/${projectId}`).then(r => {
      setImages((r.data || []).filter(d => d.doc_type === "survey" && d.file_url));
    }).catch(() => {});
  }, [projectId]);

  if (!images.length) return null;
  return (
    <section className="swiss-card-flat p-6" data-testid="design-images-gallery">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label">Imported design images</div>
          <div className="text-xs muted mt-1">
            {images.length} page{images.length === 1 ? "" : "s"} captured from the SolarEdge proposal. Visible to the customer on the quote &amp; portal pages.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {images.map(img => {
          const fid = (img.file_url || "").split("/").pop();
          // Same-origin URLs use the httpOnly cookie automatically; external
          // pre-signed URLs are used as-is. No URL-bound token leakage.
          const src = img.file_url && img.file_url.startsWith("http")
            ? img.file_url
            : `${base}/api/files/${fid}`;
          return (
            <a key={img.id} href={src} target="_blank" rel="noreferrer" className="block border border-[var(--line)] overflow-hidden hover-lift" data-testid={`design-img-${img.id}`}>
              <img src={src} alt={img.name} className="w-full h-36 object-cover" loading="lazy" />
              <div className="text-[10px] muted px-2 py-1 bg-[var(--surface)] truncate">{img.name}</div>
            </a>
          );
        })}
      </div>
    </section>
  );
}


/**
 * Horizontal "variants" tab row. Visible only when 2+ design variants exist.
 * Lets the user switch which variant is primary (used by quote, monitoring, KPIs).
 */
function DesignVariantsBar({ projectId, currentDesignId, onSwitched }) {
  const [variants, setVariants] = React.useState([]);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/designs/variants/${projectId}`);
      setVariants(data || []);
    } catch {
      // ignore
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load, currentDesignId]);

  const setPrimary = async (vid) => {
    setBusyId(vid);
    try {
      await api.post(`/designs/${vid}/set-primary`);
      toast.success("Primary variant switched");
      await load();
      onSwitched && onSwitched();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (vid) => {
    if (variants.length <= 1) {
      toast.error("Cannot delete the only design variant");
      return;
    }
    if (!window.confirm("Delete this design variant? The numbers and images will be removed.")) return;
    setBusyId(vid);
    try {
      await api.delete(`/designs/${vid}`);
      toast.success("Variant deleted");
      await load();
      onSwitched && onSwitched();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (variants.length < 2) return null;

  return (
    <section className="swiss-card-flat p-4" data-testid="design-variants-bar">
      <div className="flex items-center justify-between mb-3">
        <div className="label">Design variants ({variants.length})</div>
        <div className="muted text-xs">Click a variant to set it as primary for the quote &amp; monitoring.</div>
      </div>
      <div className="flex gap-2 overflow-x-auto">
        {variants.map((v) => {
          const isPrimary = !!v.is_primary;
          return (
            <div
              key={v.id}
              className="border rounded-md px-3 py-2 min-w-[180px]"
              style={{
                borderColor: isPrimary ? "var(--accent)" : "var(--line)",
                background: isPrimary ? "var(--accent-soft)" : "#fff",
              }}
              data-testid={`variant-card-${v.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm truncate">{v.variant_name || "Variant"}</span>
                {isPrimary && <span className="tag signal !text-[9px] !py-0.5">primary</span>}
              </div>
              <div className="font-mono text-[11px] muted mb-2">
                {v.systemSize ? `${v.systemSize} kWp` : "—"} · {v.annualEnergyProduction ? `${Math.round(v.annualEnergyProduction)} kWh` : "—"}
              </div>
              <div className="flex gap-1.5">
                {!isPrimary && (
                  <button
                    type="button"
                    onClick={() => setPrimary(v.id)}
                    disabled={busyId === v.id}
                    className="btn-ghost !py-1 !px-2 !text-[10px]"
                    data-testid={`variant-make-primary-${v.id}`}
                  >
                    Make primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(v.id)}
                  disabled={busyId === v.id}
                  className="btn-ghost !py-1 !px-2 !text-[10px]"
                  data-testid={`variant-delete-${v.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


